/**
 * Momsdeklaration box mapping (Skatteverket's rutor).
 *
 *   - account -> default momskod (the box the account's period movement lands in)
 *   - momskod -> label, section and which side of the ledger the amount is read from
 *   - box 49 formula
 *   - the VAT accounts the compiler and the momsomföring use
 *
 * The momsrapport (workstream C) reads `meta.vat.momskod` per transaction and
 * falls back to `defaultMomskodFor(account)`. `scripts/import-bas.py` carries a
 * mirror of `MOMSKOD_BY_RANGE` so the bundled BAS data can expose the same
 * default; src/test/bas.test.ts asserts they agree.
 */
import type { Ore } from '../money';
import type { Momskod, VatChoice, VatRate } from '../types';

export type MomsSection = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I';

export interface MomsBoxInfo {
  code: Momskod | '49';
  /** Skatteverket's wording, slightly shortened. */
  label: string;
  section: MomsSection;
  /**
   * Which side the box amount is read from: sales and output VAT are credit
   * balances (reported positive), purchases and input VAT are debit balances.
   */
  amountFrom: 'debit' | 'credit';
}

export const MOMS_SECTION_LABELS: Readonly<Record<MomsSection, string>> = {
  A: 'Momspliktig försäljning eller uttag exklusive moms',
  B: 'Utgående moms på försäljning eller uttag',
  C: 'Momspliktiga inköp där köparen är skattskyldig',
  D: 'Utgående moms på inköp i ruta 20–24',
  E: 'Försäljning m.m. som är undantagen från moms',
  F: 'Ingående moms',
  G: 'Moms att betala eller få tillbaka',
  H: 'Import',
  I: 'Utgående moms på import',
};

export const MOMS_BOXES: readonly MomsBoxInfo[] = [
  { code: '05', label: 'Momspliktig försäljning som inte ingår i annan ruta', section: 'A', amountFrom: 'credit' },
  { code: '06', label: 'Momspliktiga uttag', section: 'A', amountFrom: 'credit' },
  { code: '07', label: 'Beskattningsunderlag vid vinstmarginalbeskattning', section: 'A', amountFrom: 'credit' },
  { code: '08', label: 'Hyresinkomster vid frivillig skattskyldighet', section: 'A', amountFrom: 'credit' },
  { code: '10', label: 'Utgående moms 25 %', section: 'B', amountFrom: 'credit' },
  { code: '11', label: 'Utgående moms 12 %', section: 'B', amountFrom: 'credit' },
  { code: '12', label: 'Utgående moms 6 %', section: 'B', amountFrom: 'credit' },
  { code: '20', label: 'Inköp av varor från ett annat EU-land', section: 'C', amountFrom: 'debit' },
  { code: '21', label: 'Inköp av tjänster från ett annat EU-land enligt huvudregeln', section: 'C', amountFrom: 'debit' },
  { code: '22', label: 'Inköp av tjänster från ett land utanför EU', section: 'C', amountFrom: 'debit' },
  { code: '23', label: 'Inköp av varor i Sverige som köparen är skattskyldig för', section: 'C', amountFrom: 'debit' },
  { code: '24', label: 'Övriga inköp av tjänster som köparen är skattskyldig för', section: 'C', amountFrom: 'debit' },
  { code: '30', label: 'Utgående moms 25 %', section: 'D', amountFrom: 'credit' },
  { code: '31', label: 'Utgående moms 12 %', section: 'D', amountFrom: 'credit' },
  { code: '32', label: 'Utgående moms 6 %', section: 'D', amountFrom: 'credit' },
  { code: '35', label: 'Försäljning av varor till ett annat EU-land', section: 'E', amountFrom: 'credit' },
  { code: '36', label: 'Försäljning av varor utanför EU', section: 'E', amountFrom: 'credit' },
  { code: '39', label: 'Försäljning av tjänster till näringsidkare i annat EU-land enligt huvudregeln', section: 'E', amountFrom: 'credit' },
  { code: '40', label: 'Övrig försäljning av tjänster omsatta utanför Sverige', section: 'E', amountFrom: 'credit' },
  { code: '41', label: 'Försäljning när köparen är skattskyldig i Sverige', section: 'E', amountFrom: 'credit' },
  { code: '42', label: 'Övrig försäljning m.m.', section: 'E', amountFrom: 'credit' },
  { code: '48', label: 'Ingående moms att dra av', section: 'F', amountFrom: 'debit' },
  { code: '49', label: 'Moms att betala eller få tillbaka', section: 'G', amountFrom: 'credit' },
  { code: '50', label: 'Beskattningsunderlag vid import', section: 'H', amountFrom: 'debit' },
  { code: '60', label: 'Utgående moms 25 %', section: 'I', amountFrom: 'credit' },
  { code: '61', label: 'Utgående moms 12 %', section: 'I', amountFrom: 'credit' },
  { code: '62', label: 'Utgående moms 6 %', section: 'I', amountFrom: 'credit' },
];

const boxByCode: ReadonlyMap<string, MomsBoxInfo> = new Map(MOMS_BOXES.map((b) => [b.code, b]));

export function momsBoxInfo(code: Momskod | '49'): MomsBoxInfo {
  const info = boxByCode.get(code);
  if (!info) throw new RangeError(`Unknown momskod ${code}`);
  return info;
}

// ---- Account defaults -------------------------------------------------------

/** Same table as `MOMSKOD_BY_RANGE` in scripts/import-bas.py. */
export const MOMSKOD_BY_RANGE: readonly (readonly [min: number, max: number, code: Momskod])[] = [
  [3000, 3003, '05'], [3004, 3004, '42'], [3011, 3011, '05'],
  [3105, 3105, '36'], [3108, 3108, '35'],
  [3231, 3231, '41'], [3305, 3305, '40'], [3308, 3308, '39'],
  [3910, 3912, '42'], [3913, 3913, '08'], [3914, 3914, '05'],
  [2610, 2613, '10'], [2614, 2614, '30'], [2615, 2615, '60'], [2616, 2616, '10'],
  [2620, 2623, '11'], [2624, 2624, '31'], [2625, 2625, '61'], [2626, 2626, '11'],
  [2630, 2633, '12'], [2634, 2634, '32'], [2635, 2635, '62'], [2636, 2636, '12'],
  [2640, 2642, '48'], [2645, 2647, '48'], [2649, 2649, '48'],
  [4415, 4417, '23'], [4425, 4427, '24'],
  [4515, 4517, '20'], [4531, 4533, '22'], [4535, 4537, '21'], [4545, 4547, '50'],
];

/** Default momsdeklaration box for an account, or undefined when it reports nothing. */
export function defaultMomskodFor(accountNumber: number): Momskod | undefined {
  for (const [min, max, code] of MOMSKOD_BY_RANGE) {
    if (accountNumber >= min && accountNumber <= max) return code;
  }
  return undefined;
}

// ---- VAT accounts used by the compiler --------------------------------------

export const INPUT_VAT_ACCOUNT = 2640;
export const REVERSE_CHARGE_INPUT_VAT_ACCOUNT = 2645;
export const VAT_SETTLEMENT_ACCOUNT = 2650;
export const VAT_RECEIVABLE_ACCOUNT = 1650;
export const ROUNDING_ACCOUNT = 3740;

/** Accounts the momsomföring clears against 2650: 2610–2649 (2650 itself excluded). */
export function isVatClearingAccount(accountNumber: number): boolean {
  return accountNumber >= 2610 && accountNumber <= 2649;
}

/** Utgående moms on domestic sales by rate: 2610 / 2620 / 2630. */
export function outputVatAccountFor(rate: VatRate): number {
  switch (rate) {
    case 25:
      return 2610;
    case 12:
      return 2620;
    case 6:
      return 2630;
    case 0:
      throw new RangeError('No output VAT account for 0 %');
    default: {
      const exhaustive: never = rate;
      throw new RangeError(`Unknown VAT rate ${String(exhaustive)}`);
    }
  }
}

/** Utgående moms under omvänd betalningsskyldighet by rate: 2614 / 2624 / 2634. */
export function reverseChargeOutputAccountFor(rate: VatRate): number {
  switch (rate) {
    case 25:
      return 2614;
    case 12:
      return 2624;
    case 6:
      return 2634;
    case 0:
      throw new RangeError('No reverse charge VAT account for 0 %');
    default: {
      const exhaustive: never = rate;
      throw new RangeError(`Unknown VAT rate ${String(exhaustive)}`);
    }
  }
}

export function outputVatBoxFor(rate: VatRate): Momskod {
  switch (rate) {
    case 25:
      return '10';
    case 12:
      return '11';
    case 6:
      return '12';
    case 0:
      throw new RangeError('No output VAT box for 0 %');
    default: {
      const exhaustive: never = rate;
      throw new RangeError(`Unknown VAT rate ${String(exhaustive)}`);
    }
  }
}

export function reverseChargeOutputBoxFor(rate: VatRate): Momskod {
  switch (rate) {
    case 25:
      return '30';
    case 12:
      return '31';
    case 6:
      return '32';
    case 0:
      throw new RangeError('No reverse charge VAT box for 0 %');
    default: {
      const exhaustive: never = rate;
      throw new RangeError(`Unknown VAT rate ${String(exhaustive)}`);
    }
  }
}

/**
 * The box the *net amount* of a transaction is reported in, given the VAT
 * choice. Purchases with Swedish VAT report no base (only box 48 via 2640).
 * `undefined` = outside the declaration (e.g. medlemsavgifter, mode 'none').
 */
export function baseMomskodFor(kind: 'purchase' | 'sale', vat: Pick<VatChoice, 'rate' | 'mode'>): Momskod | undefined {
  switch (vat.mode) {
    case 'domestic':
      if (kind === 'sale') return vat.rate === 0 ? '42' : '05';
      return undefined;
    case 'none':
      return undefined;
    case 'rc_services_eu':
      return kind === 'sale' ? '39' : '21';
    case 'rc_services_non_eu':
      return kind === 'sale' ? '40' : '22';
    case 'rc_goods_eu':
      return kind === 'sale' ? '35' : '20';
    default: {
      const exhaustive: never = vat.mode;
      throw new RangeError(`Unknown VAT mode ${String(exhaustive)}`);
    }
  }
}

export function isReverseCharge(mode: VatChoice['mode']): boolean {
  return mode === 'rc_services_eu' || mode === 'rc_services_non_eu' || mode === 'rc_goods_eu';
}

// ---- Box 49 -----------------------------------------------------------------

export const BOX_49_PLUS: readonly Momskod[] = ['10', '11', '12', '30', '31', '32', '60', '61', '62'];
export const BOX_49_MINUS: readonly Momskod[] = ['48'];

/** Whole kronor: Skatteverket wants amounts without öre (truncated toward zero). */
export function truncateToKronor(amount: Ore): Ore {
  return Math.trunc(amount / 100) * 100;
}

/**
 * Box 49 = (10 + 11 + 12 + 30 + 31 + 32 + 60 + 61 + 62) − 48. Positive = moms
 * att betala, negative = moms att få tillbaka. Inputs are box amounts as they
 * are declared (positive numbers, whole kronor or öre; the result keeps the
 * unit of the inputs).
 */
export function computeBox49(boxes: Partial<Record<Momskod, Ore>>): Ore {
  let total = 0;
  for (const code of BOX_49_PLUS) total += boxes[code] ?? 0;
  for (const code of BOX_49_MINUS) total -= boxes[code] ?? 0;
  return total;
}
